import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useGestaoUsuarios } from '../context/GestaoUsuariosProvider';
import { ArrowLeft, Building, Save, User, Mail, Phone, MapPin, Key } from 'lucide-react';

const CadastroColaborador = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { 
    criarColaborador, 
    atualizarColaborador, 
    obterColaborador,
    setores,
    carregarSetores,
    loading 
  } = useGestaoUsuarios();

  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    setor: '',
    email_pessoal: '',
    senha_temporaria: ''
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const isEditing = !!id;

  useEffect(() => {
    carregarSetores();
    
    if (isEditing) {
      loadColaborador();
    }
  }, [id, isEditing]);

  const loadColaborador = async () => {
    try {
      const colaborador = await obterColaborador(id);
      if (colaborador) {
        setFormData({
          nome: colaborador.nome || '',
          email: colaborador.email || '',
          telefone: colaborador.celular || '',
          setor: colaborador.setor || '',
          email_pessoal: colaborador.email_pessoal || '',
          senha_temporaria: '' // Não carregar senha na edição
        });
      }
    } catch (error) {
      console.error('Erro ao carregar colaborador:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpar erro do campo quando usuário começar a digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (!formData.setor) {
      newErrors.setor = 'Setor é obrigatório';
    }

    if (!isEditing && !formData.senha_temporaria) {
      newErrors.senha_temporaria = 'Senha temporária é obrigatória';
    } else if (!isEditing && formData.senha_temporaria.length < 6) {
      newErrors.senha_temporaria = 'Senha deve ter pelo menos 6 caracteres';
    }

    if (formData.email_pessoal && !/\S+@\S+\.\S+/.test(formData.email_pessoal)) {
      newErrors.email_pessoal = 'Email pessoal inválido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      const dadosColaborador = {
        nome: formData.nome.trim(),
        email: formData.email.trim(),
        telefone: formData.telefone.trim() || null,
        setor: parseInt(formData.setor),
        email_pessoal: formData.email_pessoal.trim() || null,
        senha_temporaria: formData.senha_temporaria || null
      };

      if (isEditing) {
        await atualizarColaborador(id, dadosColaborador);
      } else {
        await criarColaborador(dadosColaborador);
      }

      navigate('/gestao-usuarios/colaboradores');
    } catch (error) {
      console.error('Erro ao salvar colaborador:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="gestao-usuarios cadastro-full-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gestao-usuarios cadastro-full-container">
      <div className="cadastro-header">
        <button
          onClick={() => navigate('/gestao-usuarios/colaboradores')}
          className="back-button"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="header-content">
          <h1>
            <Building size={32} />
            {isEditing ? 'Editar Colaborador' : 'Novo Colaborador'}
          </h1>
          <p>
            {isEditing 
              ? 'Atualize as informações do colaborador' 
              : 'Cadastre um novo colaborador da organização'
            }
          </p>
        </div>
      </div>

      <div className="cadastro-content-wrapper">
        <form onSubmit={handleSubmit} className="cadastro-form">
          <div className="form-grid">
            {/* Informações Básicas */}
            <div className="form-section">
              <h3>Informações Básicas</h3>
              
              <div className="form-group">
                <label htmlFor="nome">
                  <User size={16} />
                  Nome Completo *
                </label>
                <input
                  type="text"
                  id="nome"
                  name="nome"
                  value={formData.nome}
                  onChange={handleInputChange}
                  className={errors.nome ? 'error' : ''}
                  placeholder="Digite o nome completo"
                />
                {errors.nome && (
                  <span className="error-message">
                    {errors.nome}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="email">
                  <Mail size={16} />
                  Email Corporativo *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={errors.email ? 'error' : ''}
                  placeholder="email@empresa.com"
                />
                {errors.email && (
                  <span className="error-message">
                    {errors.email}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="email_pessoal">
                  <Mail size={16} />
                  Email Pessoal
                </label>
                <input
                  type="email"
                  id="email_pessoal"
                  name="email_pessoal"
                  value={formData.email_pessoal}
                  onChange={handleInputChange}
                  className={errors.email_pessoal ? 'error' : ''}
                  placeholder="email.pessoal@gmail.com"
                />
                {errors.email_pessoal && (
                  <span className="error-message">
                    {errors.email_pessoal}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="telefone">
                  <Phone size={16} />
                  Telefone/Celular
                </label>
                <input
                  type="tel"
                  id="telefone"
                  name="telefone"
                  value={formData.telefone}
                  onChange={handleInputChange}
                  placeholder="(27) 99999-9999"
                />
              </div>
            </div>

            {/* Informações Organizacionais */}
            <div className="form-section">
              <h3>Informações Organizacionais</h3>
              
              <div className="form-group">
                <label htmlFor="setor">
                  <MapPin size={16} />
                  Setor *
                </label>
                <select
                  id="setor"
                  name="setor"
                  value={formData.setor}
                  onChange={handleInputChange}
                  className={errors.setor ? 'error' : ''}
                >
                  <option value="">Selecione um setor</option>
                  {setores.map(setor => (
                    <option key={setor.id} value={setor.id}>
                      {setor.descricao}
                    </option>
                  ))}
                </select>
                {errors.setor && (
                  <span className="error-message">
                    {errors.setor}
                  </span>
                )}
              </div>

              {!isEditing && (
                <div className="form-group">
                  <label htmlFor="senha_temporaria">
                    <Key size={16} />
                    Senha Temporária *
                  </label>
                  <input
                    type="password"
                    id="senha_temporaria"
                    name="senha_temporaria"
                    value={formData.senha_temporaria}
                    onChange={handleInputChange}
                    className={errors.senha_temporaria ? 'error' : ''}
                    placeholder="Mínimo 6 caracteres"
                    minLength={6}
                  />
                  {errors.senha_temporaria && (
                    <span className="error-message">
                      {errors.senha_temporaria}
                    </span>
                  )}
                  <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    O colaborador deverá alterar a senha no primeiro acesso
                  </small>
                </div>
              )}
            </div>
          </div>

          <div className="form-actions">
            <Link 
              to="/gestao-usuarios/colaboradores" 
              className="button secondary"
            >
              Cancelar
            </Link>
            <button 
              type="submit" 
              className="button primary"
              disabled={saving}
            >
              <Save size={16} />
              {saving 
                ? (isEditing ? 'Salvando...' : 'Cadastrando...') 
                : (isEditing ? 'Salvar Alterações' : 'Cadastrar Colaborador')
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CadastroColaborador;